import React from 'react'
import { useTranslation } from 'react-i18next'
import { DedicatedServerCreateOptions } from '../../../api/bareMetalTypes'
import { Select } from '../../../components'
import { handleOsIcons } from '../../../hooks/useOsIcons'
import { Card, CardHead, CardTitle, ErrorLine, Hint, Mono, OsGrid, OsTile } from './ui'

type OperatingSystem = DedicatedServerCreateOptions['os'][number]
type OsGroup = { key: string; title: string; icon: string; versions: OperatingSystem[] }

// Dedicated OS metadata sometimes identifies AlmaLinux/Rocky as CentOS and
// Proxmox/Oracle as Debian/CentOS. Use the product name before that fallback.
const families = [
  { key: 'almalinux', title: 'AlmaLinux', pattern: /^alma\s*linux\b/i },
  { key: 'rockylinux', title: 'Rocky Linux', pattern: /^rocky\s*linux\b/i },
  { key: 'centos', title: 'CentOS', pattern: /^centos\b/i },
  { key: 'debian', title: 'Debian', pattern: /^debian\b/i },
  { key: 'ubuntu', title: 'Ubuntu', pattern: /^ubuntu\b/i },
  { key: 'windows', title: 'Windows', pattern: /^windows\b/i },
  { key: 'fedora', title: 'Fedora', pattern: /^fedora\b/i },
  { key: 'proxmox', title: 'Proxmox VE', pattern: /^proxmox\b/i },
  { key: 'oraclelinux', title: 'Oracle Linux', pattern: /^oracle\s*linux\b/i },
  { key: 'cloudlinux', title: 'CloudLinux', pattern: /^cloud\s*linux\b/i },
  { key: 'openvz', title: 'OpenVZ', pattern: /^openvz\b/i },
]

export const groupOperatingSystems = (systems: OperatingSystem[]): OsGroup[] => {
  const groups = new Map<string, OsGroup>()
  systems.forEach(os => {
    const name = os.name.trim()
    const family = families.find(item => item.pattern.test(name))
    const distro = os.os_distro?.trim().toLowerCase() || ''
    const key = family?.key || distro || name
    const group = groups.get(key)
    if (group) group.versions.push(os)
    else groups.set(key, {
      key,
      title: family?.title || name.replace(/\s+\d.*$/, '') || distro,
      icon: handleOsIcons(family?.key || distro),
      versions: [os],
    })
  })
  return [...groups.values()]
}

type Props = {
  systems: OperatingSystem[]
  value: string
  onChange: (id: string) => void
  error?: string
}

const fallbackIcon = handleOsIcons('')

export default function OperatingSystemSelector({ systems, value, onChange, error }: Props) {
  const [t] = useTranslation()
  const groups = groupOperatingSystems(systems)
  const selected = groups.find(group => group.versions.some(os => String(os.id) === value))

  return <Card data-validation-field="os" data-validation-error={error ? true : undefined}
    tabIndex={error ? -1 : undefined} aria-invalid={Boolean(error)} aria-labelledby="dedicated-os-title" $error={Boolean(error)}>
    <CardHead>
      <CardTitle id="dedicated-os-title">{t('OS')}</CardTitle>
      {error && <ErrorLine role="alert">{error}</ErrorLine>}
    </CardHead>
    {groups.length ? <>
      <OsGrid role="group" aria-label={String(t('OS'))} data-test-id="dedicated-os-select">
        {groups.map(group => <OsTile key={group.key} type="button" $active={group.key === selected?.key}
          aria-pressed={group.key === selected?.key}
          onClick={() => { if (group.key !== selected?.key) onChange(String(group.versions[0].id)) }}>
          {group.icon && group.icon !== fallbackIcon ? <img src={group.icon} alt="" /> : <Mono aria-hidden="true">{group.title.slice(0, 2).toUpperCase()}</Mono>}
          <span>{group.title}</span>
        </OsTile>)}
      </OsGrid>
      {selected && <Select title={t('VERSION')} fieldName="os" selectedValue={value}
        customStyles={{ width: 'min(360px, 100%)' }} onChange={id => onChange(String(id))}
        data={selected.versions.map(os => ({ value: String(os.id), label: os.name }))} />}
    </> : <Hint>{t('DEDICATED_NO_COMPONENT_OPTIONS')}</Hint>}
  </Card>
}
